import { PAdbContext } from "./PAdbContext";
import {
    IUsrContext,
    IUsrContextChangeEvent,
    IUsrContextLoadOptions,
    IUsrPropertyChangeEvent
} from "./PA.Core.IF.User";

import {
    esWB_Company,
    esWB_SalesArea, esWB_SalesOrg,
    esWB_Session,
    esWB_User,
    esWB_User_Group,
    esWB_User_Group_Customer,
    esWB_User_Group_Product,
    esWB_User_Group_Role,
    esWB_User_Group_SalesArea,
    esWB_User_Group_User
} from "./PAdbSchema";
import {
    WB_BusinessUnitRow,
    WB_CompanyRow,
    WB_CustomerRow,
    WB_DomainItemRow,
    WB_MasterDataSetRow, WB_PartitionRow,
    WB_PlanManagementRow,
    WB_SalesAreaRow,
    WB_SalesOrgRow, WB_SessionRow,
    WB_UserGroupRow,
    WB_UserRow
} from "./PAdbSchemaRows";

import * as _ from "lodash";
import { ASync } from "./Basic/ASync";
import { ProgressProvider } from "./Basic/Progress";
import { IProgressProvider } from "./PA.Core.IF.Basic";
import { Idle } from "idlejs";
import { ESRecordResolver } from "./lib/ESRecordResolver";
import axios from "axios";
import { PAEnvironment } from "./PAEnvironment";
import { Messenger } from "./Basic/Messenger";
import { ISessionContext } from "./PA.Core.IF.Session";
import { PASearch } from "./PASearch";
import { BehaviorSubject } from "rxjs";

export interface PAusrContextCreateOptions {
    iTimeoutPeriodSeconds?: number,

    [id: string]: any
}


export class PAusrContext
    implements IUsrContext {

    private _dbContext: PAdbContext;
    private _wbAuthUsrId: number;
    private _user: WB_UserRow;
    private _customers: WB_CustomerRow[];
    private _businessUnits: WB_BusinessUnitRow[];
    private _roles: WB_DomainItemRow[];
    private _dctRoleById: { [id: string]: WB_DomainItemRow };
    private _custRoles: { [id: number]: WB_DomainItemRow[] };
    private _dctCustRoleByCustIdByRoleId: { [wb_cust_id: number]: { [role_id: string]: WB_DomainItemRow } };
    private _dctRoleBySalesOrgIdByRoleId: { [wb_salesorg_id: number]: { [role_id: string]: WB_DomainItemRow } };

    private _planmanagements: WB_PlanManagementRow[];
    private _usergroups: WB_UserGroupRow[];
    private _masterDataSets: WB_MasterDataSetRow[];
    private _companies: WB_CompanyRow[];
    private _salesOrgs: WB_SalesOrgRow[];
    private _salesAreas: WB_SalesAreaRow[];

    private _ctxWB_MasterDataSetRow: WB_MasterDataSetRow
    private _ctxWB_CompanyRow: WB_CompanyRow
    private _ctxWB_SalesOrgRow: WB_SalesOrgRow
    private _ctxWB_SalesAreaRow: WB_SalesAreaRow

    private _bIsLoaded: boolean;
    private _OnLoadComplete: Promise<PAusrContext>;
    private _LoadProgressProvider: ProgressProvider;
    private _OnContextChange: BehaviorSubject<IUsrContextChangeEvent>
    private _OnPropertyChange: BehaviorSubject<IUsrPropertyChangeEvent>

    private _bIsValid: boolean;
    private _iTimeoutPeriodSeconds = 60 * 15;

    constructor(options?: PAusrContextCreateOptions) {
        let opts: PAusrContextCreateOptions = options || {};
        this._dbContext = new PAdbContext({});
        this._wbAuthUsrId = 0;
        this._user = undefined;
        this._customers = [];
        this._businessUnits = [];
        this._roles = [];
        this._dctRoleById = {};
        this._custRoles = {};
        this._dctCustRoleByCustIdByRoleId = {};
        this._dctRoleBySalesOrgIdByRoleId = {};
        this._usergroups = [];
        this._planmanagements = [];

        this._bIsLoaded = false;
        this._OnLoadComplete = undefined;
        this._LoadProgressProvider = new ProgressProvider();
        this._OnContextChange = new BehaviorSubject<IUsrContextChangeEvent>(undefined);
        this._OnPropertyChange = new BehaviorSubject<IUsrPropertyChangeEvent>(undefined);

        if ("iTimeoutPeriodSeconds" in opts) {
            this._iTimeoutPeriodSeconds = opts.iTimeoutPeriodSeconds;
        }

        this._bIsValid = false;
    }

    get Context_WB_MasterDataSetRow(): WB_MasterDataSetRow {
        return this._ctxWB_MasterDataSetRow;
    }

    get Context_WB_CompanyRow(): WB_CompanyRow {
        return this._ctxWB_CompanyRow;
    }

    get Context_WB_SalesOrgRow(): WB_SalesOrgRow {
        return this._ctxWB_SalesOrgRow;
    }

    get Context_WB_SalesAreaRow(): WB_SalesAreaRow {
        return this._ctxWB_SalesAreaRow;
    }

    setEnterpriseContext(wbSalesAreaId: number): boolean {
        const self = this;

        let saRow = _.first(_.filter(self._salesAreas, (sa) => sa.cif.WB_SALESAREA_ID == wbSalesAreaId));
        if (_.isEmpty(saRow))
            return false
        let wbSalesOrgId = saRow.cif.WB_SALESORG_ID;
        let soRow = _.first(_.filter(self._salesOrgs, (so) => so.cif.WB_SALESORG_ID == wbSalesOrgId));
        if (_.isEmpty(soRow))
            return false
        let wbCompanyId = soRow.cif.WB_COMPANY_ID;
        let coRow = _.first(_.filter(self._companies, (co) => co.cif.WB_COMPANY_ID == wbCompanyId));
        if (_.isEmpty(coRow))
            return false
        let wbMDSetId = coRow.cif.WB_MDSET_ID
        let mdsRow = _.first(_.filter(self._masterDataSets, (mds) => mds.cif.WB_MDSET_ID == wbMDSetId));
        if (_.isEmpty(mdsRow))
            return false

        self._ctxWB_MasterDataSetRow = mdsRow
        self._ctxWB_CompanyRow = coRow
        self._ctxWB_SalesOrgRow = soRow
        self._ctxWB_SalesAreaRow = saRow

        self.OnContextChange.next({ ctx: self, change: "EnterpriseContext" })
        return true
    }

    async saveEnterpriseContext(): Promise<void> {
        const self = this

        let usr = this._user
        if (_.isEmpty(usr))
            return

        //
        usr.cif.WB_MDSET_ID_CTX = self._ctxWB_MasterDataSetRow?.cif?.WB_MDSET_ID || 0
        usr.cif.WB_COMPANY_ID_CTX = self._ctxWB_CompanyRow?.cif?.WB_COMPANY_ID || 0
        usr.cif.WB_SALESORG_ID_CTX = self._ctxWB_SalesOrgRow?.cif?.WB_SALESORG_ID || 0
        usr.cif.WB_SALESAREA_ID_CTX = self._ctxWB_SalesAreaRow?.cif?.WB_SALESAREA_ID || 0

        await self.dbContext.Save(esWB_User, usr)
    }

    get bIsLoaded(): boolean {
        return this._bIsLoaded;
    }

    get OnLoadComplete(): Promise<IUsrContext> {
        if (this.bIsLoaded) {
            return ASync.resolve(this);
        }

        if (!_.isEmpty(this._OnLoadComplete)) {
            return this._OnLoadComplete;
        }

        return ASync.error("AppCtx Load not initiated or failed.");
    }

    get dbContext(): PAdbContext {
        return this._dbContext;
    }

    get User(): WB_UserRow {
        return this._user;
    }

    set User(value: WB_UserRow) {
        this._user = value;
    }

    get OnUserPropertyChange(): BehaviorSubject<IUsrPropertyChangeEvent> {
        return this._OnPropertyChange
    }

    private notifyPropertyChange(record: WB_UserRow, propertyName: string, propertyValue: any) {
        this._OnPropertyChange.next({
            ctx: this,
            Row: record,
            PropertyChanged: propertyName,
            PropertyValue: propertyValue
        })
    }

    get Roles(): WB_DomainItemRow[] {
        return this._roles;
    }

    get Customers(): WB_CustomerRow[] {
        return this._customers;
    }

    get BusinessUnits(): WB_BusinessUnitRow[] {
        return this._businessUnits;
    }

    get PlanManagements(): WB_PlanManagementRow[] {
        return this._planmanagements;
    }

    get UserGroups(): WB_UserGroupRow[] {
        return this._usergroups;
    }

    get WB_MasterDataSets(): WB_MasterDataSetRow[] {
        return this._masterDataSets;
    }

    get WB_Companies(): WB_CompanyRow[] {
        return this._companies;
    }

    get WB_SalesOrgs(): WB_SalesOrgRow[] {
        return this._salesOrgs;
    }

    get WB_SalesAreas(): WB_SalesAreaRow[] {
        return this._salesAreas;
    }

    get LoadProgressProvider(): IProgressProvider {
        return this._LoadProgressProvider;
    }

    Load(options: IUsrContextLoadOptions): Promise<IUsrContext> {
        const self = this;

        const opts: IUsrContextLoadOptions = <any>options || {};

        self.LoadProgressProvider.start("Loading Session");

        self._wbAuthUsrId = opts.wbAuthUsrId || self.User?.cif?.WB_AUTHUSR_ID || -1;

        let returnValue = self._OnLoadComplete = (async () => {

            try {
                let usrRecordResolver: ESRecordResolver = new ESRecordResolver(self.dbContext,
                    {
                        standardConditions: {
                            [esWB_User_Group_Customer]: { "DELETED_IND": { "notIn": ["1"] } },
                            [esWB_User_Group_SalesArea]: { "DELETED_IND": { "notIn": ["1"] } },
                            [esWB_User_Group_Product]: { "DELETED_IND": { "notIn": ["1"] } }
                        },
                        dctExcludeHasManyNamedRelations: {
                            [esWB_User]: [esWB_Session],
                            [esWB_User_Group]: [esWB_User_Group_User]
                        },
                        dctIncludeBelongsToNamedRelations: {
                            [esWB_SalesArea]: [esWB_SalesOrg],
                            [esWB_SalesOrg]: [esWB_Company]
                        }
                    });

                await usrRecordResolver.findAllWithRelationships(esWB_User, [self._wbAuthUsrId]);

                let userRow: WB_UserRow = <any>usrRecordResolver.get(esWB_User, self._wbAuthUsrId);
                userRow.setupOnChangeEvents([], self.notifyPropertyChange.bind(self), self)

                if (_.isEmpty(userRow)) {
                    self.LoadProgressProvider.exception("User not found during record resolution: " + self._wbAuthUsrId);
                    return self;
                }

                self.User = userRow;

                let usergroupIds = [];

                let results = await PASearch.find("vUI_UserGroup_ChildrenHierarchy",
                    { where: { WB_AUTHUSR_ID: self._wbAuthUsrId } },
                    { WB_AUTHUSR_ID: { type: "number" } },
                    [],
                    0,
                    ["WB_USRGRP_ID"]);

                usergroupIds = _.map(results, "WB_USRGRP_ID");

                let usrUserGroups = <WB_UserGroupRow[]>await usrRecordResolver.findAllWithRelationships(esWB_User_Group, usergroupIds);

                let usrRoles: WB_DomainItemRow[] = [];
                let usrCustomers: WB_CustomerRow[] = [];
                let salesOrgs: WB_SalesOrgRow[] = [];
                let salesAreas: WB_SalesAreaRow[] = [];
                let companies: WB_CompanyRow[] = [];
                let masterDataSets: WB_MasterDataSetRow[] = [];
                _.forEach(usrUserGroups, (aUserGroup: WB_UserGroupRow) => {
                    let groupCustomers = _.flatMap(_.filter(aUserGroup.WB_UserGroupCustomerRows, (aRow) => !_.isEmpty(aRow.WB_CustomerRow)), (a) => a.WB_CustomerRow);
                    let groupRoles = _.flatMap(_.filter(aUserGroup.WB_UserGroupRoleRows, (rel) => !_.isEmpty(rel.WB_DomainItemRow_Role)), (a) => a.WB_DomainItemRow_Role);
                    usrCustomers = usrCustomers.concat(groupCustomers);

                    let groupSalesAreas = _.flatMap(aUserGroup.WB_UserGroupSalesAreaRows, (aRow) => aRow.WB_SalesAreaRow);

                    if (aUserGroup.WB_MasterDataSetRow)
                        masterDataSets = masterDataSets.concat(aUserGroup.WB_MasterDataSetRow);

                    if (aUserGroup.WB_SalesOrgRow) {
                        salesOrgs.push(aUserGroup.WB_SalesOrgRow);
                        if (aUserGroup.WB_SalesOrgRow.WB_CompanyRow)
                            companies.push(aUserGroup.WB_SalesOrgRow.WB_CompanyRow);

                        //roles granted via a group scoped to a specific Sales Org only count as granted "for the active Sales Org"
                        let wbSalesOrgId = aUserGroup.WB_SalesOrgRow.cif.WB_SALESORG_ID;
                        if (!(wbSalesOrgId in self._dctRoleBySalesOrgIdByRoleId)) {
                            self._dctRoleBySalesOrgIdByRoleId[wbSalesOrgId] = {};
                        }
                        _.forEach(groupRoles, (aRole) => {
                            self._dctRoleBySalesOrgIdByRoleId[wbSalesOrgId][aRole.cif.DOMAIN_ITEM_ID] = aRole;
                        });
                    } else {
                        //not scoped to a Sales Org, so it's granted regardless of the active Sales Org
                        usrRoles = usrRoles.concat(groupRoles);
                    }
                    salesAreas = salesAreas.concat(groupSalesAreas);


                    //add the roles by customers that are assigned to this group
                    _.forEach(usrCustomers, (aCustomer) => {
                        if (!(aCustomer.cif.WB_CUST_ID in self._dctCustRoleByCustIdByRoleId)) {
                            self._dctCustRoleByCustIdByRoleId[aCustomer.cif.WB_CUST_ID] = {};
                        }
                        _.forEach(groupRoles, (aRole) => {
                            self._dctCustRoleByCustIdByRoleId[aCustomer.cif.WB_CUST_ID][aRole.cif.DOMAIN_ITEM_ID] = aRole;
                        });
                    });

                });
                usrRoles = _.uniqBy(usrRoles, (aRole: WB_DomainItemRow) => aRole.cif.WB_DOMITEM_ID);
                self._customers = _.uniqBy(usrCustomers, (aCust) => aCust.cif.WB_CUST_ID);
                self._masterDataSets = _.uniqBy(masterDataSets, (aSet) => aSet.cif.WB_MDSET_ID);
                self._companies = _.uniqBy(companies, (aCompany) => aCompany.cif.WB_COMPANY_ID);
                self._salesOrgs = _.uniqBy(salesOrgs, (aSalesOrg) => aSalesOrg.cif.WB_SALESORG_ID);
                self._salesAreas = _.uniqBy(salesAreas, (aSalesArea) => aSalesArea.cif.WB_SALESAREA_ID);

                self.setEnterpriseContext(userRow.cif.WB_SALESAREA_ID_CTX)
                if (_.isEmpty(self.Context_WB_SalesAreaRow)) {
                    let firstSA = _.first(this._salesAreas)
                    if (firstSA)
                        self.setEnterpriseContext(firstSA.cif.WB_SALESAREA_ID)
                }

                const isActive = (row: any) => row?.cif?.DELETED_IND != "1";

                if (_.isEmpty(self.Context_WB_MasterDataSetRow)) {
                    self._ctxWB_MasterDataSetRow =
                        _.find(self._masterDataSets, (mds) => mds.cif.WB_MDSET_ID == userRow.cif.WB_MDSET_ID_CTX && isActive(mds))
                        || _.find(self._masterDataSets, isActive);
                }

                if (_.isEmpty(self.Context_WB_CompanyRow)) {
                    let wbMDSetId = self.Context_WB_MasterDataSetRow?.cif?.WB_MDSET_ID;
                    self._ctxWB_CompanyRow =
                        _.find(self._companies, (co) => co.cif.WB_COMPANY_ID == userRow.cif.WB_COMPANY_ID_CTX && co.cif.WB_MDSET_ID == wbMDSetId && isActive(co))
                        || _.find(self._companies, (co) => co.cif.WB_MDSET_ID == wbMDSetId && isActive(co));
                }

                if (_.isEmpty(self.Context_WB_SalesOrgRow)) {
                    let wbCompanyId = self.Context_WB_CompanyRow?.cif?.WB_COMPANY_ID;
                    self._ctxWB_SalesOrgRow =
                        _.find(self._salesOrgs, (so) => so.cif.WB_SALESORG_ID == userRow.cif.WB_SALESORG_ID_CTX && so.cif.WB_COMPANY_ID == wbCompanyId && isActive(so))
                        || _.find(self._salesOrgs, (so) => so.cif.WB_COMPANY_ID == wbCompanyId && isActive(so));
                }

                usrCustomers = _.filter(usrCustomers, (aRow) => !_.isEmpty(aRow));

                //roles are also assigned to the user groups, so do the same thing as for customers
                let usrBusinessUnitRel = _.filter(userRow.WB_UsrBusinessUnitRows, (aRow) => !_.isEmpty(aRow.WB_BusinessUnitRow));
                let usrBusinessUnits = _.map(usrBusinessUnitRel, (aRow) => aRow.WB_BusinessUnitRow);
                self._businessUnits = usrBusinessUnits;

                let usrPlanMgmts: WB_PlanManagementRow[] = _.map(userRow.WB_PlanManagementUserRows, (a) => a.WB_PlanManagementRow);
                usrPlanMgmts = _.filter(usrPlanMgmts, (aRow) => !_.isEmpty(aRow));

                let usrgrpCustomers: WB_CustomerRow[] = _.map(_.flatMap(usrUserGroups, (ug) => ug.WB_UserGroupCustomerRows), (ugc) => ugc.WB_CustomerRow);

                //skip groups scoped to a specific Sales Org here too, their roles are tracked in _dctRoleBySalesOrgIdByRoleId instead
                let usrGrpRoleRel = _.filter(_.flatMap(_.filter(usrUserGroups, (ug) => !ug.WB_SalesOrgRow), (ug) => ug.WB_UserGroupRoleRows), (ugr) => !_.isEmpty(ugr.WB_DomainItemRow_Role));
                let usrgrpRoles: WB_DomainItemRow[] = _.map(_.filter(usrGrpRoleRel, (aRel) => aRel.cif.WB_CUST_ID == 0), (ugr) => ugr.WB_DomainItemRow_Role);

                let usrGrpPlanMgmts: WB_PlanManagementRow[] = []; // TODO: Fix when we add  User Groups to Plan Management records

                self._usergroups = usrUserGroups;
                self._customers = _.sortBy(_.uniqBy(_.filter(_.union(usrCustomers, usrgrpCustomers), (f) => !_.isEmpty(f)), (u) => u.cif.WB_CUST_ID), (s: WB_CustomerRow) => s.cif.CUSTOMER_NAME);
                self._roles = _.sortBy(_.uniqBy(_.filter(_.union(usrRoles, usrgrpRoles), (f) => !_.isEmpty(f)), (u) => u.cif.WB_DOMITEM_ID), (s: WB_DomainItemRow) => s.cif.DOMAIN_ITEM_NAME);
                self._planmanagements = _.sortBy(_.uniqBy(_.filter(_.union(usrPlanMgmts, usrGrpPlanMgmts), (f) => !_.isEmpty(f)), (u) => u.cif.WB_PLNMGMT_ID), (s: WB_PlanManagementRow) => s.cif.PLNMGMT_NAME);

                self._dctRoleById = _.keyBy(self._roles, (aRole) => aRole.cif.DOMAIN_ITEM_ID);

                _.forEach(self._dctCustRoleByCustIdByRoleId, (value, key) => {
                    let anIdx = _.toNumber(key);
                    self._custRoles[anIdx] = _.values(value);
                });

                if ("iTimeoutPeriodSeconds" in opts)
                    self._iTimeoutPeriodSeconds = opts.iTimeoutPeriodSeconds;

                // Setup Activity Timeout Logout, and Peer Logout
                if (!PAEnvironment.isNode()) {

                    //delete the concurrent sessions by admin user
                    let isAdmin = (self.User.cif.AUTHUSR_TYPE == 'Administrator') ? true : false
                    if (isAdmin) {
                        let arrExistingValidSessions: WB_SessionRow[] = await self.dbContext.findAll(esWB_Session, {
                            where: {
                                WB_AUTHUSR_ID: self._wbAuthUsrId,
                                DELETED_IND: { neq: '1' }
                            }
                        })
                        if (arrExistingValidSessions.length > 1) {
                            for (let x in arrExistingValidSessions) {
                                arrExistingValidSessions[x].markDeleted()
                                await self.dbContext.Save(esWB_Session, arrExistingValidSessions[x]);
                            }

                            alert("Concurrent login for admin account is not allowed. All sessions have been logged out. Please login again.")
                            await self.LogOut();
                        }
                    }

                    //
                    const idle = new Idle();

                    if (self._iTimeoutPeriodSeconds > 0) {
                        idle
                            .whenNotInteractive()
                            .within(self._iTimeoutPeriodSeconds, 1000)
                            .do(() => {
                                if (self.bIsLoaded) {
                                    self.LogOut();
                                }
                            })
                            .start();
                    }

                    if (self.User.cif.LOGOFF_POLICY == "CloseAllSessions") {
                        let msgr = new Messenger<{
                            messageType: string,
                            wbAuthUsrId?: number,
                            hostName?: string
                        }>({ InBoxEnabled: true, ChannelId: "UserLogOff-" + self.User.cif.WB_AUTHUSR_ID });
                        msgr.OnInBoxMessageReceived.subscribe((a) => {
                            if (_.isEmpty(a))
                                return;
                            switch (a.message?.messageType) {
                                case "LogOut":
                                    if (self.bIsLoaded) {
                                        msgr.disableInBox();
                                        self.LogOut();
                                    }
                            }
                        });
                    }

                }

                self._bIsLoaded = true;
                self._bIsValid = true;
                self.LoadProgressProvider.finish();
                return self;

            } catch (err) {
                self.LoadProgressProvider.exception(err);
                return self;
            }

        })();

        return returnValue;

    }

    get OnContextChange(): BehaviorSubject<IUsrContextChangeEvent> {
        return this._OnContextChange
    }

    hasRoleId(sRoleId: string, aWBCustId?: number): boolean {

        if (!this.User) {
            return false;
        }

        let aCustId = aWBCustId || 0;

        if (aCustId == 0) {
            //no customer specified: the role must be granted for the active Sales Org (or granted with no Sales Org restriction)
            if (sRoleId in this._dctRoleById) {
                return true;
            }

            let aSalesOrgId = this._ctxWB_SalesOrgRow?.cif?.WB_SALESORG_ID || 0;
            return (aSalesOrgId > 0)
                && (aSalesOrgId in this._dctRoleBySalesOrgIdByRoleId)
                && (sRoleId in this._dctRoleBySalesOrgIdByRoleId[aSalesOrgId]);
        }

        //customer specified: the user must have access to that customer, and have been granted the specified role
        let hasCustomerAccess = _.some(this._customers, (aCustomer) => aCustomer.cif.WB_CUST_ID == aCustId);
        return hasCustomerAccess && this.hasRoleIdForAnyCustomer(sRoleId);
    }

    hasRoleIdForAnyCustomer(sRoleId: string): boolean {
        return this.hasRoleId(sRoleId, 0);
    }


    async LogOut(): Promise<IUsrContext> {
        const self = this;

        try {
            self._bIsValid = false;

            if (PAEnvironment.isNode())
                return

            let sLogoutURL = "";
            if (self._user && self._user.WB_AuthProviderRow) {
                sLogoutURL = self._user.WB_AuthProviderRow.cif.AUTH_LOGOUT_URL;
            }

            let logoutEndPt = "/logout";
            let sessionCtx: ISessionContext = PAEnvironment.getValue(PAEnvironment.SessionContext);

            if (!_.isNull(sessionCtx) && !_.isUndefined(sessionCtx)) {
                sessionCtx.Session?.markDeleted()
                let updatedSession = await self.dbContext.Save(esWB_Session, sessionCtx.Session);
            }

            await axios.get(logoutEndPt,
                {
                    params: {
                        wbAuthUsrId: self.User.cif.WB_AUTHUSR_ID || 0,
                        logOffPolicy: self.User.cif.LOGOFF_POLICY || ""
                    }
                });

            document.cookie = "PA-REST-GUID=;expires=Thu, 01 Jan 1970 00:00:01 GMT;";
            if (self.User?.cif?.LOGOFF_POLICY == "CloseAllSessions") {
                let msgr = new Messenger<{ messageType: string, wbAuthUsrId?: number, hostName?: string }>({
                    InBoxEnabled: false,
                    ChannelId: "UserLogOff-" + self.User.cif.WB_AUTHUSR_ID
                });
                msgr.postMessage({
                    messageType: "LogOut",
                    wbAuthUsrId: self.User.cif.WB_AUTHUSR_ID,
                    hostName: window.location.hostname
                });

            }

            self.OnContextChange.next({ ctx: self, change: "LoginStatus" })

            if (!_.isEmpty(sLogoutURL)) {
                window.location.replace(encodeURI(sLogoutURL));
            } else {
                // If there is a popup blocker, this will not fire.
                window.location.replace(encodeURI(location.origin));
            }


        } catch (err) {
            console.log("Logoff Error: ", err);
        } finally {
            // eslint-disable-next-line no-unsafe-finally
            return self;
        }

    }

    Validate(): Promise<IUsrContext> {
        // If we want to expire from the server instead of timer
        return ASync.resolve(this);
    }

    get bIsValid(): boolean {
        return this._bIsValid;
    }

}



